import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Zap, Brain, TrendingUp, Shield, Users } from "lucide-react";

const values = [
  { icon: Brain, title: "AI-First Thinking", desc: "We don't use AI as a gimmick. It's embedded in every workflow, every decision, every deliverable.", color: "#4F8EF7" },
  { icon: Target, title: "Precision Over Volume", desc: "We'd rather send one perfect message to 10 ideal prospects than spray 10,000 with noise.", color: "#8B5CF6" },
  { icon: TrendingUp, title: "Data-Backed Growth", desc: "Gut feeling has no place here. Every strategy is built on data, measured rigorously, and iterated continuously.", color: "#4F8EF7" },
  { icon: Shield, title: "Radical Transparency", desc: "You'll always know exactly what we're doing, why, and what results it's generating. No black boxes.", color: "#8B5CF6" },
  { icon: Zap, title: "Speed and Agility", desc: "The market moves fast. We move faster. AI gives us the ability to execute at a pace no traditional agency can match.", color: "#4F8EF7" },
  { icon: Users, title: "Client Partnership", desc: "We're not vendors. We're growth partners. Your success metrics are our success metrics.", color: "#8B5CF6" },
];

const About = () => {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="about-page"
      style={{ backgroundColor: "#0a0a0f", paddingTop: "5rem" }}
    >
      {/* Hero */}
      <section className="section-padding" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-xs font-mono uppercase tracking-widest mb-3"
                style={{ color: "#4F8EF7" }}
              >
                About EnGenius Marketing
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading font-bold text-4xl sm:text-5xl text-white mb-5 leading-tight"
                data-testid="about-heading"
              >
                We Engineer <span className="gradient-text">Marketing</span> That Scales
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-base leading-relaxed mb-5"
                style={{ color: "#a1a1aa" }}
              >
              EnGenius Marketing was born from a frustration: traditional marketing agencies move too slowly, cost too much, and deliver too little. We built a new kind of agency, one that combines the strategic rigour of the best consulting firms with the velocity and intelligence of AI.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-base leading-relaxed mb-8"
                style={{ color: "#a1a1aa" }}
              >
                Since 2022, we've helped 50+ brands across SaaS, fintech, and e-commerce build marketing engines that compound: growing organic traffic, filling pipelines, and building category authority with AI at the core.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Link
                  to="/contact"
                  data-testid="about-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white text-sm transition-all duration-300"
                  style={{ background: "linear-gradient(135deg, #4F8EF7, #8B5CF6)", boxShadow: "0 0 20px rgba(79,142,247,0.3)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 0 35px rgba(79,142,247,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 0 20px rgba(79,142,247,0.3)"; }}
                >
                  Work with Us <ArrowRight size={15} />
                </Link>
              </motion.div>
            </div>

            {/* Right: image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              style={{ borderRadius: "20px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/f8936331-82f6-497f-871a-606e003b0793/images/e26f842fea32f06e8901174968966a2fc146b6d12badf918f6e8c187c2b9a24d.png"
                alt="EnGenius Marketing team working together"
                style={{ width: "100%", display: "block" }}
                data-testid="about-team-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" style={{ backgroundColor: "#080810" }}>
        <div className="container-custom">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-mono uppercase tracking-widest mb-3"
              style={{ color: "#8B5CF6" }}
            >
              Our Principles
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="font-heading font-bold text-4xl md:text-5xl text-white"
            >
              What <span className="gradient-text">Drives Us</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.09 }}
                  whileHover={{ y: -6 }}
                  data-testid={`value-card-${i}`}
                  className="p-6 rounded-2xl"
                  style={{
                    background: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${v.color}35`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${v.color}15`, border: `1px solid ${v.color}25` }}
                  >
                    <Icon size={20} style={{ color: v.color }} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-semibold text-white text-base mb-2">{v.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#71717a" }}>{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Banner */}
      <section className="section-padding" style={{ backgroundColor: "#080810" }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center relative overflow-hidden rounded-3xl px-8 py-14"
            style={{
              background: "linear-gradient(135deg, rgba(79,142,247,0.1) 0%, rgba(139,92,246,0.08) 100%)",
              border: "1px solid rgba(79,142,247,0.15)",
            }}
          >
            <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "#4F8EF7" }}>Our Mission</p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-4 max-w-3xl mx-auto">
              To make world-class marketing <span className="gradient-text">accessible to every ambitious brand</span>, powered by AI, guided by strategy.
            </h2>
          </motion.div>
        </div>
      </section>
    </motion.main>
  );
};

export default About;
